import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import type { ClientConfiguration as ClientConfigurationType } from "../../../types/clientConfig";
import Modal from "../../modal-new";

interface AssignClientConfigurationTemplateModalProps {
  isVisible: boolean;
  assignableTemplates: ClientConfigurationType[];
  modalTemplateId: string;
  setModalTemplateId: (id: string) => void;
  setShowTemplateModal: (show: boolean) => void;
  onAssign: () => void;
}

export const AssignClientConfigurationTemplateModal = ({
  isVisible,
  assignableTemplates,
  modalTemplateId,
  setModalTemplateId,
  setShowTemplateModal,
  onAssign,
}: AssignClientConfigurationTemplateModalProps) => {
  const { t } = useTranslation(["athlete-portal", "common"]);

  if (!isVisible) return null;

  const close = () => setShowTemplateModal(false);

  const handleOverlayClose = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    close();
  };

  return (
    <Modal
      className="assign-client-configuration-template-modal"
      title={t("clientConfiguration.assignTemplateModal.title")}
      onClose={handleOverlayClose}
    >
      <div className="body">
        {assignableTemplates.length === 0 ? (
          <span>{t("clientConfiguration.assignTemplateModal.noTemplates")}</span>
        ) : (
          <aside className="assign-client-configuration-template-modal__template-list">
            <span>{t("clientConfiguration.assignTemplateModal.selectLabel")}</span>
            <ul>
              {assignableTemplates.map(
                (item: ClientConfigurationType, i: number) => (
                  <li
                    key={`${item.templateName}-${i}-column`}
                    aria-current={
                      modalTemplateId === item._id ? true : undefined
                    }
                    onClick={() => setModalTemplateId(item._id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setModalTemplateId(item._id);
                      }
                    }}
                  >
                    <p className="p1">
                      {item.templateName}
                      {item.templateType === "global" && (
                        <span className="p3">
                          {t(
                            "clientConfiguration.assignTemplateModal.globalSuffix"
                          )}
                        </span>
                      )}
                    </p>
                  </li>
                )
              )}
            </ul>
          </aside>
        )}
      </div>
      <footer className="client-config-footer-buttons">
        <div className="right-content">
          <button type="button" data-button-type="secondary" onClick={close}>
            {t("common:cancel")}
          </button>
          <button
            type="button"
            data-button-type="primary"
            disabled={!modalTemplateId}
            onClick={onAssign}
          >
            {t("clientConfiguration.assignTemplateModal.assign")}
          </button>
        </div>
      </footer>
    </Modal>
  );
};
